import React, { useEffect, useRef, useState } from 'react';
import { MdExpandMore, MdSend } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useLabels } from '../contexts/LanguageContext';
import { getActiveUserInfo } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';
import { useSetPageHeader } from '../contexts/PageHeaderContext';

interface Subject {
  id: number;
  title: string;
  status: number;
}

interface SubjectResponse {
  code: number;
  status: boolean;
  message: string;
  data: Subject[];
}

const SubmitRequestPage: React.FC = () => {
  usePageMeta({
    title: 'Submit Request | HCAE Client Portal',
    description: 'Submit a concern or request to the HCAE team and get support from within the client portal.',
  });
  useSetPageHeader('Submit Request', 'Send a concern or request to our team');

  const labels = useLabels();
  const user = getActiveUserInfo();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<number | ''>('');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => { fetchSubjects(); }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${BASE_URL}subject/list`, {
        headers: { 'Content-Type': 'application/json', token: `${user?.api_token}` },
      });
      const data: SubjectResponse = await response.json();
      if (!response.ok) { toast.error(data?.message || 'Failed to load concerns'); return; }
      setSubjects(data?.data?.filter((item) => item.status === 1) || []);
    } catch {
      toast.error('Failed to load concerns. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!selectedSubject || !message.trim()) {
      toast.warning('Please select a concern and enter your message');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}ticket/store`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token: `${user?.api_token}` },
        body: JSON.stringify({ description: message.trim(), subject_id: selectedSubject }),
      });
      const result = await response.json();
      if (!response.ok) { toast.error(result?.message || 'Request failed.'); return; }
      if (result?.status === true || result?.code === 200) {
        toast.success(result?.message || 'Request submitted successfully');
        setMessage('');
        setSelectedSubject('');
      } else {
        toast.error(result?.message || 'Request could not be submitted');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const selectedLabel = subjects.find((s) => s.id === selectedSubject)?.title || '';

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-5">


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
        {/* Form card */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-3 sm:p-6 space-y-3 sm:space-y-6">
          {/* Subject dropdown */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              {labels.lbl_concern}
            </label>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl border-2 border-slate-200 bg-white text-left focus:outline-none focus:border-brand-500 hover:border-brand-300 transition-colors"
              >
                <span className={selectedLabel ? 'text-slate-800 font-medium text-sm' : 'text-slate-400 text-sm'}>
                  {selectedLabel || `Select ${labels.lbl_concern}`}
                </span>
                <MdExpandMore
                  size={20}
                  className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOpen && (
                <div className="absolute z-20 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                  {subjects.length === 0 ? (
                    <div className="px-4 py-3 text-slate-400 text-sm">No options available</div>
                  ) : (
                    subjects.map((subject) => (
                      <button
                        key={subject.id}
                        type="button"
                        onClick={() => { setSelectedSubject(subject.id); setIsOpen(false); }}
                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${selectedSubject === subject.id
                          ? 'bg-brand-50 text-brand-700'
                          : 'text-slate-700 hover:bg-slate-50'
                          }`}
                      >
                        {subject.title}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Message textarea */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              {labels.lbl_message}
            </label>
            <textarea
              placeholder="Describe your concern or request in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 bg-white focus:outline-none focus:border-brand-500 hover:border-brand-300 resize-none text-slate-800 placeholder-slate-300 text-sm leading-relaxed transition-colors"
            />
          </div>

          {/* Submit button */}
          <div className="flex justify-center sm:justify-end">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-8 py-3 bg-brand-700 text-white text-sm font-semibold rounded-xl hover:bg-brand-800 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-brand-200"
            >
              <MdSend size={16} />
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>

        {/* Info panel */}
        <div className="space-y-4">
          <div className="bg-brand-50 border border-brand-100 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-brand-800 mb-2">How it works</h3>
            <ul className="text-xs text-brand-600 space-y-2 leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-brand-200 flex items-center justify-center text-brand-700 font-bold shrink-0 mt-0.5 text-[9px]">1</span>
                Select the category that best describes your concern.
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-brand-200 flex items-center justify-center text-brand-700 font-bold shrink-0 mt-0.5 text-[9px]">2</span>
                Provide a detailed description of your request.
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-brand-200 flex items-center justify-center text-brand-700 font-bold shrink-0 mt-0.5 text-[9px]">3</span>
                Our team will review and respond as soon as possible.
              </li>
            </ul>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Need urgent help?</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              For urgent matters, please contact us directly at{' '}
              <a href="mailto:info@iheart-hc.com" className="text-brand-600 hover:underline font-medium">
                info@iheart-hc.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitRequestPage;
